// @flow

import React, { memo, useCallback, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { useLangFile } from 'js/context/LanguageContext';
import Styles from './FieldIntegrationMappingSelector.module.less';
import Typography from '@mui/material/Typography';
import { ArrowDropDown, ArrowDropUp } from '@mui/icons-material';
import Box from '@mui/material/Box';
import { ClickAwayListener, List, Paper, Popper, Button } from '@mui/material';
import FieldIntegrationMappingSelectorListItem from './FieldIntegrationMappingSelectorListItem';
import { voidFunc } from '../../constants/PropTypeUtils';
import { getFieldName } from 'js/helpers/StateInterpreters';

const FieldIntegrationMappingSelector = (props: FieldIntegrationMappingSelector.propTypes) => {
  const LangFile = useLangFile();
  const [anchorEl, setAnchorEl] = useState(null);
  const divRef = useRef();
  const open = Boolean(anchorEl);
  const { fieldItems, selectedField, onSelectField } = props;

  const handleClick = useCallback(() => {
    setAnchorEl(divRef.current);
  }, [anchorEl]);

  const handleListItemClick = useCallback(
    (field) => {
      onSelectField(field);
      handleClickAway();
    },
    [onSelectField]
  );

  const handleClickAway = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <Box
        ref={divRef}
        onClick={handleClick}
        className={Styles.root}
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%',
          height: '100%',
        }}
      >
        {!selectedField && (
          <Box
            sx={{
              pl: 1,
            }}
          >
            <Typography style={{ color: 'red' }}>
              {LangFile.FieldIntegrationMapping.select}
            </Typography>
          </Box>
        )}
        {selectedField && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              p: 1,
            }}
          >
            <Typography className={Styles.text} style={{ fontSize: '10px' }}>
              {selectedField.mef}
            </Typography>
            <Typography className={Styles.text} style={{ fontWeight: 500 }}>
              {getFieldName(selectedField, LangFile)}
            </Typography>
            <Typography className={Styles.text}>{LangFile.CropType[selectedField.crop]}</Typography>
            <Typography className={Styles.text}>{selectedField.size.toFixed(2) + ' ha'}</Typography>
          </Box>
        )}
        {open ? <ArrowDropUp /> : <ArrowDropDown />}
      </Box>
      {open && (
        <ClickAwayListener onClickAway={handleClickAway}>
          <Popper
            style={{ zIndex: 1300, minWidth: 220 }}
            open={true}
            anchorEl={anchorEl}
            placement="bottom-start"
          >
            <Paper>
              <Box className={Styles.PopoverContainer}>
                <Box
                  sx={{
                    width: '100%',
                    height: '30px',
                    display: 'flex',
                    flexDirection: 'row',
                  }}
                >
                  <Box
                    className={Styles.header}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      width: '25%',
                      height: '100%',
                    }}
                  >
                    {LangFile.FieldIntegrationMapping.match}
                  </Box>
                  <Box
                    style={{ marginLeft: '6px' }}
                    className={Styles.header}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      height: '100%',
                    }}
                  >
                    {LangFile.FieldSettingsDialog.fieldName}
                  </Box>
                </Box>
                <List className={Styles.list} dense={true}>
                  {fieldItems.map((fieldItem) => {
                    return (
                      <FieldIntegrationMappingSelectorListItem
                        key={fieldItem.field.fieldId}
                        fieldItem={fieldItem}
                        isSelected={
                          selectedField ? fieldItem.field.fieldId === selectedField.fieldId : false
                        }
                        onClick={handleListItemClick}
                      />
                    );
                  })}
                </List>
                <Box
                  sx={{
                    height: '40px',
                    display: 'flex',
                    justifyContent: 'center',
                  }}
                >
                  <Button
                    disabled={!selectedField}
                    onClick={() => {
                      handleListItemClick(null);
                    }}
                  >
                    {LangFile.FieldIntegrationMapping.clearSelection}
                  </Button>
                </Box>
              </Box>
            </Paper>
          </Popper>
        </ClickAwayListener>
      )}
    </>
  );
};

FieldIntegrationMappingSelector.propTypes = {
  fieldItems: PropTypes.array,
  selectedField: PropTypes.object,
  onSelectField: PropTypes.func,
};

FieldIntegrationMappingSelector.defaultProps = {
  fieldItems: [],
  onSelectField: voidFunc,
};

export default memo(FieldIntegrationMappingSelector);
